import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, List, LayoutGrid, Columns, Square, ExternalLink, CalendarPlus, Rss, Copy } from "lucide-react";

const TYPE_COLORS = {
  "First day": "bg-cc-green/10 text-cc-green border-cc-green/30",
  "Rotation start (All Clear)": "bg-cc-green/10 text-cc-green border-cc-green/30",
  "Video appointment": "bg-cc-orange/10 text-cc-orange border-cc-orange/30",
  "Touchpoint": "bg-cc-orange/10 text-cc-orange border-cc-orange/30",
  "School event": "bg-cc-blue/10 text-cc-blue border-cc-blue/30",
  "Meeting": "bg-cc-blue/10 text-cc-blue border-cc-blue/30",
  "Paperwork due date": "bg-red-50 text-red-700 border-red-200",
  "Placement end date": "bg-zinc-100 text-zinc-600 border-zinc-200",
};
const DOT = {
  "First day": "bg-cc-green", "Rotation start (All Clear)": "bg-cc-green",
  "Video appointment": "bg-cc-orange", "Touchpoint": "bg-cc-orange", "School event": "bg-cc-blue",
  "Meeting": "bg-cc-blue",
  "Paperwork due date": "bg-red-500", "Placement end date": "bg-zinc-400",
};
// Milestone/date types show in the all-day band; the rest are placed in hourly slots.
const ALLDAY_TYPES = new Set(["First day", "Rotation start (All Clear)", "Paperwork due date", "Placement end date"]);
const HOURS = Array.from({ length: 14 }, (_, i) => i + 7); // 7 AM .. 8 PM
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MO = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const keyOf = (iso) => (iso ? ymd(new Date(iso)) : "undated");
const sameDay = (a, b) => ymd(a) === ymd(b);
const hourLabel = (h) => `${((h + 11) % 12) + 1} ${h < 12 ? "AM" : "PM"}`;
const timeLabel = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const isAllDay = (e) => ALLDAY_TYPES.has(e.event_type);

export default function Calendar() {
  const { user } = useAuth();
  const canCreate = ["Super Admin", "Campus Coordinator", "Campus Coordinator Supervisor"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("month");
  const [anchor, setAnchor] = useState(new Date());
  const [selected, setSelected] = useState(new Date());
  const [disabled, setDisabled] = useState(new Set());
  const [subOpen, setSubOpen] = useState(false);
  const [createAt, setCreateAt] = useState(null); // Date for a clicked slot

  const load = () => api.get("/calendar-events").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const presentTypes = useMemo(() => Array.from(new Set(rows.map((e) => e.event_type))).sort(), [rows]);
  const rowsF = useMemo(() => rows.filter((e) => !disabled.has(e.event_type)), [rows, disabled]);
  const byDay = useMemo(() => {
    const m = {};
    rowsF.forEach((e) => { const k = keyOf(e.start_time); (m[k] = m[k] || []).push(e); });
    return m;
  }, [rowsF]);
  const toggleType = (t) => setDisabled((prev) => { const n = new Set(prev); n.has(t) ? n.delete(t) : n.add(t); return n; });

  const today = new Date();
  const nav = (dir) => {
    const d = new Date(anchor);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else if (view === "week") d.setDate(d.getDate() + dir * 7);
    else d.setDate(d.getDate() + dir);
    setAnchor(d);
    if (view !== "month") setSelected(d);
  };
  const goToday = () => { setAnchor(new Date()); setSelected(new Date()); };

  const label = view === "month" ? `${MO[anchor.getMonth()]} ${anchor.getFullYear()}`
    : view === "day" ? anchor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })
    : weekLabel(anchor);

  return (
    <div>
      <PageHeader title="Global Calendar" subtitle="Starts, video calls, touchpoints, school events, paperwork due dates & deadlines">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-9" onClick={() => setSubOpen(true)} data-testid="cal-subscribe-btn"><Rss className="w-4 h-4 mr-1.5" />Add to Calendar</Button>
          <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
            {[["month", LayoutGrid, "Month"], ["week", Columns, "Week"], ["day", Square, "Day"], ["list", List, "List"]].map(([v, Icon, lbl]) => (
              <button key={v} onClick={() => setView(v)} className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 ${view === v ? "bg-white shadow-sm text-cc-blue" : "text-zinc-500"}`} data-testid={`cal-view-${v}`}><Icon className="w-4 h-4" />{lbl}</button>
            ))}
          </div>
        </div>
      </PageHeader>

      {/* Type filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="cal-type-filters">
        <span className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">Show:</span>
        {presentTypes.map((t) => (
          <label key={t} className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid={`cal-filter-${t.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
            <Checkbox checked={!disabled.has(t)} onCheckedChange={() => toggleType(t)} />
            <span className={`w-2 h-2 rounded-full ${DOT[t] || "bg-zinc-400"}`} />
            <span className="text-zinc-600">{t}</span>
          </label>
        ))}
      </div>

      {view === "list" ? <ListView rows={rowsF} /> : (
        <Card className="p-0 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-cc-blue" />
              <h2 className="text-lg font-semibold text-zinc-800" data-testid="cal-range-label">{label}</h2>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={goToday} className="h-8 text-xs mr-1" data-testid="cal-today">Today</Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => nav(-1)} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => nav(1)} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
          {view === "month" && <MonthGrid anchor={anchor} byDay={byDay} today={today} selected={selected} setSelected={setSelected} />}
          {view === "week" && <TimeGrid days={weekDays(anchor)} byDay={byDay} today={today} onSlot={canCreate ? setCreateAt : null} />}
          {view === "day" && <TimeGrid days={[new Date(anchor)]} byDay={byDay} today={today} onSlot={canCreate ? setCreateAt : null} />}
        </Card>
      )}

      {view === "month" && <SelectedDay selected={selected} events={byDay[ymd(selected)] || []} />}

      <SubscribeDialog open={subOpen} onClose={() => setSubOpen(false)} />
      {canCreate && <CreateDialog at={createAt} onClose={() => setCreateAt(null)} onCreated={() => { setCreateAt(null); load(); }} />}
    </div>
  );
}

function MonthGrid({ anchor, byDay, today, selected, setSelected }) {
  const cells = useMemo(() => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const start = new Date(first); start.setDate(first.getDate() - first.getDay());
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [anchor]);
  return (
    <>
      <div className="grid grid-cols-7 border-b bg-zinc-50 text-[11px] font-medium uppercase tracking-wide text-zinc-400">
        {WD.map((w) => <div key={w} className="px-2 py-2 text-center">{w}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === anchor.getMonth();
          const evts = byDay[ymd(d)] || [];
          const isToday = sameDay(d, today);
          const isSel = sameDay(d, selected);
          return (
            <button key={i} onClick={() => setSelected(new Date(d))} data-testid={`cal-day-${ymd(d)}`}
              className={`min-h-[92px] border-b border-r p-1.5 text-left align-top transition-colors ${inMonth ? "bg-white" : "bg-zinc-50/60"} ${isSel ? "ring-2 ring-inset ring-cc-blue" : "hover:bg-zinc-50"}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs w-6 h-6 flex items-center justify-center rounded-full ${isToday ? "bg-cc-blue text-white font-semibold" : inMonth ? "text-zinc-700" : "text-zinc-300"}`}>{d.getDate()}</span>
                {evts.length > 3 && <span className="text-[10px] text-zinc-400">+{evts.length - 3}</span>}
              </div>
              <div className="space-y-0.5">
                {evts.slice(0, 3).map((e) => (
                  <div key={e.id} className={`text-[10px] leading-tight px-1 py-0.5 rounded border truncate ${TYPE_COLORS[e.event_type] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`} title={e.title}>{e.title}</div>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

function TimeGrid({ days, byDay, today, onSlot }) {
  const cols = days.length;
  const gridCols = { gridTemplateColumns: `64px repeat(${cols}, minmax(0,1fr))` };
  const eventsAt = (day, hour) => (byDay[ymd(day)] || []).filter((e) => !isAllDay(e) && e.start_time && new Date(e.start_time).getHours() === hour);
  const allDayAt = (day) => (byDay[ymd(day)] || []).filter(isAllDay);
  const slot = (day, hour) => { if (!onSlot) return; const d = new Date(day); d.setHours(hour, 0, 0, 0); onSlot(d); };
  return (
    <div>
      {/* Day headers */}
      <div className="grid border-b bg-zinc-50" style={gridCols}>
        <div className="px-2 py-2" />
        {days.map((d, i) => (
          <div key={i} className="px-2 py-2 text-center border-l">
            <div className="text-[11px] uppercase tracking-wide text-zinc-400">{WD[d.getDay()]}</div>
            <div className={`text-sm font-semibold ${sameDay(d, today) ? "text-cc-blue" : "text-zinc-700"}`}>{d.getDate()}</div>
          </div>
        ))}
      </div>
      {/* All-day band */}
      <div className="grid border-b" style={gridCols} data-testid="cal-allday-row">
        <div className="px-2 py-1.5 text-[10px] uppercase tracking-wide text-zinc-400 text-right">All day</div>
        {days.map((d, i) => (
          <div key={i} className="px-1 py-1.5 border-l space-y-0.5 min-h-[34px]">
            {allDayAt(d).map((e) => (
              <div key={e.id} className={`text-[10px] leading-tight px-1 py-0.5 rounded border truncate ${TYPE_COLORS[e.event_type] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`} title={e.title} data-testid={`event-${e.id}`}>{e.title}</div>
            ))}
          </div>
        ))}
      </div>
      {/* Hour rows */}
      <div className="max-h-[560px] overflow-y-auto cc-scroll">
        {HOURS.map((h) => (
          <div key={h} className="grid border-b last:border-0" style={gridCols}>
            <div className="px-2 py-1 text-[10px] text-zinc-400 text-right -mt-2">{hourLabel(h)}</div>
            {days.map((d, i) => (
              <div key={i} onClick={() => slot(d, h)} className={`border-l min-h-[52px] p-1 space-y-1 ${onSlot ? "cursor-pointer hover:bg-cc-blue/5 group" : ""}`} data-testid={`cal-slot-${ymd(d)}-${h}`}>
                {eventsAt(d, h).map((e) => (
                  <div key={e.id} onClick={(ev) => ev.stopPropagation()} className={`text-[11px] leading-tight px-1.5 py-1 rounded border ${TYPE_COLORS[e.event_type] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`} data-testid={`event-${e.id}`}>
                    <div className="font-medium truncate">{e.title}</div>
                    <div className="opacity-70">{timeLabel(e.start_time)}</div>
                    {e.link && <a href={e.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 underline">Join <ExternalLink className="w-2.5 h-2.5" /></a>}
                  </div>
                ))}
                {onSlot && eventsAt(d, h).length === 0 && <CalendarPlus className="w-3.5 h-3.5 text-cc-blue opacity-0 group-hover:opacity-60" />}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function SelectedDay({ selected, events }) {
  return (
    <Card className="mt-4">
      <div className="flex items-center gap-2 mb-3">
        <CalendarDays className="w-4 h-4 text-cc-blue" />
        <h3 className="font-semibold text-zinc-800" data-testid="cal-selected-label">{selected.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</h3>
      </div>
      {events.length === 0 ? <p className="text-sm text-zinc-400" data-testid="cal-selected-empty">No events on this day.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="cal-selected-events">
          {events.map((e) => (
            <div key={e.id} className="border rounded-md p-3" data-testid={`event-${e.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-800">{e.title}</p>
                  {e.description && <p className="text-xs text-zinc-400">{e.description}</p>}
                  {e.start_time && !isAllDay(e) && <p className="text-[11px] text-zinc-400 mt-0.5">{timeLabel(e.start_time)}</p>}
                </div>
                <span className={`shrink-0 flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border ${TYPE_COLORS[e.event_type] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`}><span className={`w-1.5 h-1.5 rounded-full ${DOT[e.event_type] || "bg-zinc-400"}`} />{e.event_type}</span>
              </div>
              {e.link && <a href={e.link} target="_blank" rel="noreferrer" className="text-xs text-cc-blue inline-flex items-center gap-1 mt-1.5">Join <ExternalLink className="w-3 h-3" /></a>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function ListView({ rows }) {
  const grouped = rows.reduce((acc, e) => {
    const k = e.start_time ? new Date(e.start_time).toLocaleDateString() : "Undated";
    (acc[k] = acc[k] || []).push(e); return acc;
  }, {});
  return (
    <Card>
      {rows.length === 0 ? <EmptyState text="No calendar events match your filters." /> : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([day, items]) => (
            <div key={day}>
              <div className="flex items-center gap-2 text-sm font-semibold text-zinc-700 mb-2"><CalendarDays className="w-4 h-4 text-cc-blue" />{day}</div>
              <div className="space-y-2 pl-6">
                {items.map((e) => (
                  <div key={e.id} className="flex items-center justify-between border rounded-md p-3" data-testid={`event-${e.id}`}>
                    <div>
                      <p className="text-sm font-medium text-zinc-800">{e.title}</p>
                      <p className="text-xs text-zinc-400">{e.description}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${TYPE_COLORS[e.event_type] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>{e.event_type}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function SubscribeDialog({ open, onClose }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!open) return;
    api.get("/calendar/subscribe").then(({ data }) => {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      setUrl(`${base}${data.path}`);
    });
  }, [open]);
  const webcal = url.replace(/^https?:\/\//, "webcal://");
  const copy = () => { navigator.clipboard?.writeText(url); toast.success("Feed link copied"); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="cal-subscribe-dialog">
        <DialogHeader>
          <DialogTitle>Subscribe to this calendar</DialogTitle>
          <DialogDescription>Add Campus Connections to Outlook, Apple Calendar or Google Calendar. It stays in sync automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input readOnly value={url} className="font-mono text-xs" data-testid="cal-feed-url" onFocus={(e) => e.target.select()} />
            <Button variant="outline" onClick={copy} data-testid="cal-copy-feed"><Copy className="w-4 h-4" /></Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={webcal} data-testid="cal-webcal-link"><Button variant="outline" size="sm"><CalendarDays className="w-4 h-4 mr-1.5" />Add to Apple / Outlook</Button></a>
            <a href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><CalendarDays className="w-4 h-4 mr-1.5" />Add to Google Calendar</Button></a>
          </div>
          <p className="text-xs text-zinc-400">Tip: in Outlook desktop use <b>Add calendar → Subscribe from web</b> and paste the link above. Keep this private link to yourself.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({ at, onClose, onCreated }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (at) {
      const iso = new Date(at.getTime() - at.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      setForm({ title: "", event_type: "Meeting", start_time: iso, description: "" });
    }
  }, [at]);
  const save = async () => {
    if (!form.title?.trim()) return toast.error("Give the event a title");
    setSaving(true);
    try {
      const start = new Date(form.start_time);
      const end = new Date(start.getTime() + 60 * 60000);
      await api.post("/calendar-events", {
        title: form.title, event_type: form.event_type, description: form.description,
        start_time: start.toISOString(), end_time: end.toISOString(),
      });
      toast.success("Added to the calendar");
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!at} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cal-create-dialog">
        <DialogHeader>
          <DialogTitle>New event</DialogTitle>
          <DialogDescription>{at ? at.toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""} · 1 hour. Sends an Outlook invite.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Title"><Input data-testid="cal-create-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Faculty sync, site visit…" /></Field>
          <Field label="Start"><Input type="datetime-local" data-testid="cal-create-start" value={form.start_time || ""} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></Field>
          <Field label="Notes (optional)"><Textarea data-testid="cal-create-notes" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <p className="text-xs text-zinc-400">To schedule a student touchpoint (with a gift card & video room), use the Touchpoints page.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-cc-blue text-white" data-testid="cal-create-save">{saving ? "Adding…" : "Add event"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function weekDays(anchor) {
  const start = new Date(anchor); start.setDate(anchor.getDate() - anchor.getDay());
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
}
function weekLabel(anchor) {
  const days = weekDays(anchor);
  const a = days[0], b = days[6];
  const sameMonth = a.getMonth() === b.getMonth();
  return sameMonth ? `${MO[a.getMonth()]} ${a.getDate()} – ${b.getDate()}, ${a.getFullYear()}`
    : `${MO[a.getMonth()]} ${a.getDate()} – ${MO[b.getMonth()]} ${b.getDate()}, ${b.getFullYear()}`;
}
